import {
    FieldConfiguration,
    FieldName,
    PeliasFeature,
    PeliasQuery,
    PeliasV1Endpoints,
} from '@symplicity/geocoding';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import AsyncCreatableSelect, { AsyncCreatableProps } from 'react-select/async-creatable';
import { Icon } from 'src/ui/shared/components/icon/Icon';
import { useThrottledCallback } from 'use-debounce';
import { locationConstants } from './location.constants';
import { LocationInnerFormData } from '../location-form.models';

const { getBoundary, getFocus } = PeliasQuery;
const { setValueOptions, getSelectStyles, featureToLabel } = locationConstants;

const parentFields: {
    [key in FieldName]: FieldName[];
} = {
    street: ['postcode', 'city', 'region'],
    postcode: ['city', 'region'],
    city: ['region'],
    region: [],
};

interface PeliasFeatureOption {
    label: React.ReactNode;
    value: string;
    feature?: PeliasFeature;
}

interface Props {
    id: string;
    field: FieldName;
    className?: string;
    required?: boolean;
}

export const PeliasLocationControl = ({
    id,
    field,
    className,
    required,
}: Props): React.ReactElement => {
    const {
        register,
        setValue,
        watch,
        getValues,
        formState: { errors },
    } = useFormContext<LocationInnerFormData>();

    const [countryCode, region, city, postcode] = watch([
        `${id}.countryCode`,
        `${id}.region`,
        `${id}.city`,
        `${id}.postcode`,
    ]);

    const peliasConfig = useMemo(() => {
        return FieldConfiguration.get(countryCode, field);
    }, [countryCode, field]);

    useEffect(() => {
        register(`${id}.${field}`, {
            validate: value => {
                if (!value) {
                    return required ? `${peliasConfig.label} is required` : undefined;
                }

                if (value.label.length > peliasConfig.maxLength) {
                    return `${peliasConfig.label} must be ${peliasConfig.maxLength} characters or less.`;
                }
                return undefined;
            },
        });
    }, [id, field, register, peliasConfig.maxLength, peliasConfig.label, required]);

    const hasError = !!errors[id] && !!errors[id]![field];

    const activeFeatures = useMemo(() => {
        return {
            region: typeof region === 'object' ? region || undefined : undefined,
            city: typeof city === 'object' ? city || undefined : undefined,
            postcode: typeof postcode === 'object' ? postcode || undefined : undefined,
        };
    }, [city, postcode, region]);

    const boundary = useMemo(() => {
        switch (field) {
            case 'region':
                return getBoundary(countryCode);
            case 'city':
                return getBoundary(countryCode, activeFeatures.region);
            case 'postcode':
                return getBoundary(
                    countryCode,
                    activeFeatures.region,
                    countryCode === 'GB' ? activeFeatures.city : undefined
                );
            case 'street':
                return getBoundary(
                    countryCode,
                    activeFeatures.region,
                    activeFeatures.city,
                    activeFeatures.postcode
                );
        }
    }, [countryCode, field, activeFeatures.city, activeFeatures.postcode, activeFeatures.region]);

    const focus = useMemo(() => {
        switch (field) {
            case 'city':
                return getFocus(activeFeatures.postcode);
            case 'postcode':
                return getFocus(activeFeatures.city);
            case 'street':
                return getFocus(activeFeatures.postcode, activeFeatures.city);
            default:
                return undefined;
        }
    }, [field, activeFeatures.city, activeFeatures.postcode]);

    const throttled = useThrottledCallback(
        (input: string, callback: (options: PeliasFeatureOption[]) => void) => {
            void PeliasV1Endpoints.autocomplete({
                text: input,
                boundary,
                focus,
                layers: peliasConfig.layers,
                api_key: 'ge-c1014ce71d3b18f6',
            }).then(features => {
                const opts: PeliasFeatureOption[] = features.map(feature => ({
                    feature,
                    value: peliasConfig.getValue(feature),
                    label: <Icon icon="geo-alt">{featureToLabel(feature)}</Icon>,
                }));

                callback(opts);
            });
        },
        300
    );

    const updateParentFieldIfNull = useCallback(
        (fieldsToUpdate: FieldName[], feature: PeliasFeature) => {
            fieldsToUpdate.forEach(fieldToUpdate => {
                const currentValue = getValues(`${id}.${fieldToUpdate}`);

                if (currentValue) {
                    return;
                }

                const { getValue } = FieldConfiguration.get(countryCode, fieldToUpdate);
                const text = getValue(feature);

                if (!text) {
                    return;
                }

                setValue(`${id}.${fieldToUpdate}`, {
                    creation: 'autofilled by pelias',
                    label: text,
                });
            });
        },
        [countryCode, getValues, id, setValue]
    );

    const clearParentFieldIfNoBBox = useCallback(
        (fieldsToUpdate: FieldName[]) => {
            for (const fieldToUpdate of fieldsToUpdate) {
                const currentValue = getValues(`${id}.${fieldToUpdate}`);

                if (
                    currentValue?.creation === 'created by user' ||
                    currentValue?.creation === 'selected from pelias'
                ) {
                    break;
                }
                setValue(`${id}.${fieldToUpdate}`, null);
            }
        },
        [getValues, id, setValue]
    );

    const onChangeInner: AsyncCreatableProps<PeliasFeatureOption, false, never>['onChange'] =
        useCallback(
            (option, meta) => {
                if (meta.action === 'create-option') {
                    setValue(
                        `${id}.${field}`,
                        {
                            creation: 'created by user',
                            label: option.value,
                        },
                        setValueOptions
                    );
                } else if (meta.action === 'clear') {
                    setValue(`${id}.${field}`, null, setValueOptions);
                    clearParentFieldIfNoBBox(parentFields[field]);
                } else if (meta.action === 'select-option') {
                    setValue(
                        `${id}.${field}`,
                        {
                            creation: 'selected from pelias',
                            label: option.value,
                            bbox: option.feature.bbox,
                        },
                        setValueOptions
                    );
                    updateParentFieldIfNull(parentFields[field], option.feature);
                    // remove bbox if converted from geolocation initially
                    setValue(`${id}.convertedBbox`, undefined);
                }
            },
            [clearParentFieldIfNoBBox, field, id, setValue, updateParentFieldIfNull]
        );

    const watchValue = watch(`${id}.${field}`);
    const valueAsOption = useMemo(() => {
        if (!watchValue) {
            return null;
        }

        return {
            label: <Icon icon="geo-alt">{watchValue.label}</Icon>,
            value: watchValue.label,
        };
    }, [watchValue]);

    const selectStyles = useMemo(() => {
        return getSelectStyles(hasError);
    }, [hasError]);

    const innerPlaceHolder = useMemo(() => {
        return required ? `${peliasConfig.label} *` : peliasConfig.label;
    }, [peliasConfig.label, required]);

    return (
        <AsyncCreatableSelect<PeliasFeatureOption, false, never>
            className={className}
            isClearable
            cacheOptions={false}
            noOptionsMessage={() => 'Type to search'}
            loadOptions={throttled}
            placeholder={innerPlaceHolder}
            onChange={onChangeInner}
            value={valueAsOption}
            {...selectStyles}
        />
    );
};
